// The ConditionalFormField children below are load-bearing: FormFields returns null for
// relationship fields, so deleting the COMPANIES child silently drops it, no build error.
import React, { ComponentType, ReactNode } from "react";

import { faPhone } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { Company, EntityType } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { RawFile } from "@mapmycustomers/shared/types/File";
import FormLayout, { LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import { FormItem, TextField } from "@mapmycustomers/ui";

import ConditionalFormField, {
  ConditionalFormFieldRenderProps,
} from "@app/component/ConditionalFormField";
import FormFields from "@app/component/FormFields";
import AddressField from "@app/component/FormFields/components/AddressField";
import PersonCompanyField from "@app/component/FormFields/components/ParentCompanyField/PersonCompanyField";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";

import File from "./components/File";

export interface PersonFormFieldsProps {
  defaultAddedFields?: LayoutSchemaField[];
  fileComponent?: ComponentType<{
    disabled?: boolean;
    onChange?: (uploadedFiles: RawFile[]) => void;
    required?: boolean;
    supportsTags?: boolean;
  }>;
  isFieldDisabled?: (field: IField) => boolean;
  layout: FormLayout;
  onAddField?: (schemaField: LayoutSchemaField) => void;
  parentCompanies: Company[];
  previewWarning?: ReactNode;
  primaryCompanyId?: Company["id"];
  setParentCompanies: (companies: Company[]) => void;
  setPrimaryCompanyId: (accountId?: Company["id"]) => void;
  skipLayoutDefaultsFor?: Set<LayoutSchemaField["field"]>;
}

const PersonFormFields = ({
  defaultAddedFields,
  fileComponent,
  isFieldDisabled,
  layout,
  onAddField,
  parentCompanies,
  previewWarning,
  primaryCompanyId,
  setParentCompanies,
  setPrimaryCompanyId,
  skipLayoutDefaultsFor,
}: PersonFormFieldsProps) => {
  return (
    <>
      {previewWarning}
      <FormItem hidden name="sourceObject" />
      <FormFields
        defaultAddedFields={defaultAddedFields}
        entityType={EntityType.PERSON}
        fileComponent={fileComponent ?? File}
        isCreateForm
        isFieldDisabled={isFieldDisabled}
        layout={layout}
        onAddField={onAddField}
        skipLayoutDefaultsFor={skipLayoutDefaultsFor}
      >
        <ConditionalFormField fieldName={PersonFieldName.PHONE}>
          {({ disabled, label, required }: ConditionalFormFieldRenderProps) => (
            <FormItem
              label={label}
              name={PersonFieldName.PHONE}
              required={required}
              rules={[{ required }]}
              showLabel={false}
            >
              <TextField
                addonBefore={<FontAwesomeIcon icon={faPhone} />}
                disabled={disabled}
                label={label}
                placeholder={label?.toString()}
                required={required}
              />
            </FormItem>
          )}
        </ConditionalFormField>

        <ConditionalFormField fieldName={PersonFieldName.COMPANIES}>
          {({ disabled, required }: ConditionalFormFieldRenderProps) => (
            <PersonCompanyField
              allowAdd={!disabled}
              disabled={disabled}
              onChange={setParentCompanies}
              onChangePrimaryCompany={setPrimaryCompanyId}
              primaryCompanyId={primaryCompanyId}
              required={required}
            />
          )}
        </ConditionalFormField>

        <ConditionalFormField feature={FieldFeature.ADDRESS}>
          {({ disabled, required }: ConditionalFormFieldRenderProps) => (
            <AddressField
              disabled={disabled}
              inheritAddressFrom={
                parentCompanies.find(({ id }) => id === primaryCompanyId) ?? parentCompanies[0]
              }
              required={required}
            />
          )}
        </ConditionalFormField>
      </FormFields>
    </>
  );
};

export default PersonFormFields;
